import React, { useEffect, useMemo, useRef, useContext } from "react";
import styled from "styled-components";
import gsap, { SplitText } from "gsap/all";
import { FontsLoadedContext } from "components/Providers";

import colors from "styles/colors";
import text from "styles/text";
import media from "styles/media";

import TwitchPNG from "assets/images/logos/twitch.png";
import XboxPNG from "assets/images/logos/xbox.png";
import RedditPNG from "assets/images/logos/reddit.png";

import StreamlabsPNG from "assets/images/logos/streamlabs.png";
import GoldmanSachsPNG from "assets/images/logos/goldmansachs.png";
import CovalentPNG from "assets/images/logos/covalent.png";

import GalaxyPNG from "assets/images/logos/galaxy.png";
import AnimocaPNG from "assets/images/logos/animca.png";
import RepublicPNG from "assets/images/logos/republic.png";
import PolygonPNG from "assets/images/logos/polygon.png";
import HashedPNG from "assets/images/logos/hashed.png";

import TamarackGlobalPNG from "assets/images/logos/tamarackglobal.png";
import SVCPNG from "assets/images/logos/svc.png";
import ThreeArrowsCapitalPNG from "assets/images/logos/threearrows.png";
import GSRPNG from "assets/images/logos/gsr.png";

import DaedalusPNG from "assets/images/logos/daedalus.png";
import EverestVenturesGroupPNG from "assets/images/logos/everest.png";
import UpsidePartnershipPNG from "assets/images/logos/upside.png";
import DoublePeakPNG from "assets/images/logos/doublepeak.png";

import AlumniVenturesPNG from "assets/images/logos/alumniventures.png";
import CipholioPNG from "assets/images/logos/cipholio.png";
import XRMMediaPNG from "assets/images/logos/xrmmedia.png";
import OvoFundPNG from "assets/images/logos/ovofund.png";

import FwbPNG from "assets/images/logos/fwb.png";
import PubgPNG from "assets/images/logos/pubg.png";
import EpicPNG from "assets/images/logos/epicgames.png";
import EndeavorPNG from "assets/images/logos/endeavor.png";

import KowloonPNG from "assets/images/logos/kowloon.png";
import NbaPNG from "assets/images/logos/nba.png";
import PinterestPNG from "assets/images/logos/pinterest.png";
import MicrosoftPNG from "assets/images/logos/microsoft.png";
import MetaPNG from "assets/images/logos/meta.png";

const investors = [
  { name: "Kevin Lin", job: "Co-founder, Twitch and CEO, Metatheory" },
  { name: "Brycent", job: "NFT Gamer, Streamer, and YouTuber, Co-Founder of Loot Squad." },
  { name: "Chief Pat", job: "Founder, CEO, Tribe Gaming" },
  { name: "Bryan Myint", job: "Co-founder & Head of Advisory, Republic Crypto" },
  { name: "Soby", job: "Co-founder, Ex Populous" },
  { name: "Mystic7", job: "Web3 Content Creator, Pokemon GO Community | Leader, YouTuber and Streamer" },
];

const Socials: React.FC = () => {
  const fontLoaded = useContext(FontsLoadedContext);

  const wrapperRef = useRef<HTMLElement | null>(null);
  const text1Ref = useRef<HTMLParagraphElement | null>(null);
  const text2Ref = useRef<HTMLParagraphElement | null>(null);
  const hr1Ref = useRef<HTMLDivElement | null>(null);
  const hr2Ref = useRef<HTMLDivElement | null>(null);
  const hr3Ref = useRef<HTMLDivElement | null>(null);
  const hr4Ref = useRef<HTMLDivElement | null>(null);
  const accent1Ref = useRef<HTMLDivElement | null>(null);
  const accent2Ref = useRef<HTMLDivElement | null>(null);
  const accent3Ref = useRef<HTMLDivElement | null>(null);
  const accent4Ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (fontLoaded) {
      const text1Child = new SplitText(text1Ref.current, {
        type: "lines",
      });

      new SplitText(text1Ref.current, {
        type: "lines",
        linesClass: "parent",
      });

      const text2Child = new SplitText(text2Ref.current, {
        type: "lines",
      });

      new SplitText(text2Ref.current, {
        type: "lines",
        linesClass: "parent",
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: wrapperRef.current,
          scrub: true,
          start: "top 70%",
          end: "bottom bottom+=10%",
        },
      });

      tl.from(
        text1Child.lines,
        {
          duration: 1,
          stagger: 1,
          y: "100%",
        },
        0
      );

      tl.from(
        accent1Ref.current,
        {
          duration: 1,
          y: "100%",
        },
        2
      );

      tl.from(
        hr1Ref.current,
        {
          duration: 2,
          width: "0%",
        },
        2
      );

      tl.from(
        ".logo1",
        {
          duration: 0.5,
          stagger: 0.5,
          y: "115%",
        },
        2
      );

      tl.from(
        text2Child.lines,
        {
          duration: 1,
          stagger: 1,
          y: "100%",
        },
        6
      );

      tl.from(
        hr3Ref.current,
        {
          duration: 2,
          width: "0%",
        },
        7
      );

      tl.from(
        accent3Ref.current,
        {
          duration: 1,
          y: "100%",
        },
        7
      );

      tl.from(
        ".investor",
        {
          duration: 0.6,
          stagger: 0.4,
          y: "150%",
        },
        7
      );

      tl.from(
        accent2Ref.current,
        {
          duration: 1,
          y: "100%",
        },
        11
      );

      tl.from(
        hr2Ref.current,
        {
          duration: 2,
          width: "0%",
        },
        11
      );

      tl.from(
        ".logo2",
        {
          duration: 0.5,
          stagger: 0.5,
          y: "110%",
          opacity: 0,
        },
        11
      );

      tl.from(
        accent4Ref.current,
        {
          duration: 1,
          y: "100%",
        },
        19
      );

      tl.fromTo(
        hr4Ref.current,
        {
          width: "0%",
        },
        {
          duration: 2,
          width: "100%",
        },
        19
      );

      tl.from(
        ".logo3",
        {
          duration: 0.5,
          stagger: 0.5,
          y: "110%",
          opacity: 0,
        },
        19
      );

      return () => {
        tl.kill();
      };
    }
  }, [fontLoaded]);

  const allinvestors = useMemo(
    () =>
      investors.map((inv, i) => {
        return (
          <HiddenWrapper key={`investor-${i}`}>
            <Investor className="investor">
              <Name>{inv.name}</Name>
              <Job>{inv.job}</Job>
            </Investor>
          </HiddenWrapper>
        );
      }),
    []
  );

  return (
    <Wrapper ref={wrapperRef}>
      <BigTitle>TEAM</BigTitle>
      <BigTitle>BACK-ERS</BigTitle>
      <Top>
        <Text ref={text1Ref}>FreshCut is led by former Twitch leaders with extensive experience across the vast multiverse of gaming creators and content.</Text>

        <OurTeam>
          <HiddenWrapper>
            <Accent ref={accent1Ref}>01 {" >> "} OUR TEAM</Accent>
          </HiddenWrapper>
          <HR ref={hr1Ref} />

          <Logos05>
            <img src={TwitchPNG} alt="twitch logo" className="logo1" loading="lazy"/>
            <img src={XboxPNG} alt="xbox logo" className="logo1" loading="lazy"/>
            <img src={RedditPNG} alt="reddit logo" className="logo1" loading="lazy"/>
          </Logos05>

          <Logos1>
            <img src={StreamlabsPNG} alt="streamlabs logo" className="logo1" loading="lazy"/>
            <img src={GoldmanSachsPNG} alt="goldman sachs logo" className="logo1" loading="lazy"/>
            <img src={CovalentPNG} alt="covalent logo" className="logo1" loading="lazy"/>
          </Logos1>
        </OurTeam>
      </Top>
      <Bottom>
        <Text ref={text2Ref}>FreshCut is backed by leading investors, advisors, and operators from the gaming, creator, and web3 sectors.</Text>
        <InvestorsWrapper>
          <HiddenWrapper>
            <Accent ref={accent3Ref}>02 {" >> "} ADVISORS</Accent>
          </HiddenWrapper>

          <HR ref={hr3Ref} />
          <Investors>{allinvestors}</Investors>
        </InvestorsWrapper>
      </Bottom>

      <HiddenWrapper>
        <Accent ref={accent2Ref}>03 {" >> "} INVESTORS</Accent>
      </HiddenWrapper>

      <HR ref={hr2Ref} />

      <Logos2>
        <img src={GalaxyPNG} alt="Galaxy logo" className="logo2" loading="lazy"/>
        <img src={AnimocaPNG} alt="Animoca logo" className="logo2" loading="lazy"/>
        <img src={RepublicPNG} alt="Republic Crypto logo" className="logo2" loading="lazy"/>
        <img src={PolygonPNG} alt="Polygon logo" className="logo2" loading="lazy"/>
        <img src={HashedPNG} className="logo2" alt="hashed logo" loading="lazy"/>
      </Logos2>

      <Logos3>
        <img src={TamarackGlobalPNG} alt="tamarack global logo" className="logo2" width="300" height="17.69" loading="lazy"/>
        <img src={SVCPNG} alt="svc logo" className="logo2" loading="lazy"/>
        <img src={ThreeArrowsCapitalPNG} alt="three arrows capital logo" className="logo2" loading="lazy"/>
        <img src={GSRPNG} alt="gsr logo" className="logo2" loading="lazy"/>
      </Logos3>

      <Logos4>
        <img src={DaedalusPNG} alt="daedalus logo" className="logo2" loading="lazy"/>
        <img src={EverestVenturesGroupPNG} alt="everest ventures group logo" className="logo2" loading="lazy"/>
        <img src={UpsidePartnershipPNG} alt="upside partnership logo" className="logo2" loading="lazy"/>
        <img src={DoublePeakPNG} alt="double peak logo" className="logo2" loading="lazy"/>
      </Logos4>

      <Logos5>
        <img src={AlumniVenturesPNG} alt="alumni ventures logo" className="logo2" loading="lazy"/>
        <img src={CipholioPNG} alt="cipholio logo" className="logo2" loading="lazy"/>
        <img src={XRMMediaPNG} alt="xrm  media logo" className="logo2" loading="lazy"/>
        <img src={OvoFundPNG} alt="ovo fund logo" className="logo2" loading="lazy"/>
      </Logos5>

      <HiddenWrapper>
        <Accent ref={accent4Ref}>04 {" >> "} ANGEL INVESTORS FROM THE FOLLOWING ORGANIZATIONS</Accent>
      </HiddenWrapper>

      <HR ref={hr4Ref} />

      <Logos6>
        <img src={FwbPNG} alt="fwb logo" className="logo3" loading="lazy"/>
        <img src={TwitchPNG} alt="twitch logo" className="logo3" loading="lazy"/>
        <img src={PubgPNG} alt="pubg logo" className="logo3" loading="lazy"/>
        <img src={EpicPNG} alt="Epic Games logo" className="logo3" loading="lazy"/>
        <img src={EndeavorPNG} alt="Endeavor logo" className="logo3" loading="lazy"/>
      </Logos6>

      <Logos7>
        <img src={KowloonPNG} alt="kowloon logo" className="logo3" loading="lazy"/>
        <img src={NbaPNG} alt="NBA logo" className="logo3" loading="lazy"/>
        <img src={PinterestPNG} alt="pinterest logo" className="logo3" loading="lazy"/>
        <img src={MicrosoftPNG} alt="microsoft logo" className="logo3" loading="lazy"/>
        <img src={MetaPNG} alt="meta logo" className="logo3" loading="lazy"/>
      </Logos7>
    </Wrapper>
  );
};

export default Socials;

const Wrapper = styled.section`
  background: ${colors.pureBlack};
  position: relative;
  color: ${colors.pureWhite};

  height: 168.82vw;
  padding: 10.42vw 13.47vw 19.44vw;

  ${media.tablet} {
    height: 199.51vw;
    padding: 12.3vw 5.86vw 19.53vw;
  }
`;

const BigTitle = styled.h2`
  ${text.desktop.h1};
  color: ${colors.black100};
  position: absolute;
  z-index: 0;
  top: 0;

  left: 4.17vw;

  :nth-of-type(2) {
    top: 35.97vw;
  }

  ${media.tablet} {
    ${text.tablet.h1}
    left: -0.98vw;
    top: -2.34vw;

    &:nth-of-type(2) {
      left: -0.78vw;
      top: 37.79vw;
    }
  }
`;

const Top = styled.div`
  display: flex;
  position: relative;
  z-index: 1;
  justify-content: space-between;

  margin-bottom: 21.39vw;

  ${media.tablet} {
    margin-bottom: 19.34vw;
  }
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;

  margin-bottom: 4.17vw;

  ${media.tablet} {
    margin-bottom: 4.88vw;
  }
`;

const Text = styled.div`
  ${text.desktop.bodyL};

  .parent {
    overflow: hidden;
  }

  width: 31.25vw;

  ${media.tablet} {
    ${text.tablet.bodyR}

    width: 29.98vw;
  }
`;

const OurTeam = styled.div`
  width: 35.83vw;

  ${media.tablet} {
    width: 50.39vw;
  }
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 2;
  overflow: hidden;
  width: 100%;
`;

const Logos05 = styled(Logos)`
  img {
    //twitch
    :nth-of-type(1) {
      width: 4.51vw;
    }

    //xbox
    :nth-of-type(2) {
      width: 5.6vw;
      height: 1.74vw;
    }

    //Reddit
    :nth-of-type(3) {
      width: 5.37vw;
    }
  }

  ${media.tablet} {
    img {
      
      // twitch
      :nth-of-type(1) {
        width: 6.35vw;
      }

       //xbox
      :nth-of-type(2) {
        width: 7.88vw;
        height: 2.44vw;
      }

      // reddit
      :nth-of-type(3) {
        width: 7.56vw;
      }
    }
  }
`;

const Logos1 = styled(Logos)`
  margin-top: 4.86vw;

  img {
    // streamlabs
    &:nth-of-type(1) {
      width: 7.97vw;
      height: 1.34vw;
    }

    // goldman sachs
    &:nth-of-type(2) {
      width: 4.21vw;
      height: 1.74vw;
    }

    // covalent
    &:nth-of-type(3) {
      width: 6.7vw;
      height: 1.64vw;
    }
  }

  ${media.tablet} {
    img {
      // streamlabs
      &:nth-of-type(1) {
        width: 11.23vw;
        height: 1.88vw;
      }

      // goldman sachs
      &:nth-of-type(2) {
        width: 5.93vw;
        height: 2.44vw;
      }

      // covalent
      &:nth-of-type(3) {
        width: 9.42vw;
        height: 2.3vw;
      }
    }
  }
`;

const Logos2 = styled(Logos)`
  margin-bottom: 4.86vw;

  img {
    // Galaxy
    :nth-of-type(1) {
      width: 4.86vw;
      height: 3.24vw;
    }

    //animoca
    :nth-of-type(2) {
      width: 4.79vw;
    }

    // republic
    :nth-of-type(3) {
      width: 6.88vw;
    }

    // polygon
    :nth-of-type(4) {
      width: 9.43vw;
      height: 1.94vw;
    }

    // hashed
    :nth-of-type(5) {
      width: 7.63vw;
    }
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {
      // Galaxy
      :nth-of-type(1) {
        width: 6.84vw;
        height: 4.56vw;
      }

      //animoca
      :nth-of-type(2) {
        width: 7.24vw;
        height: 4.2vw;
      }

      // republic
      :nth-of-type(3) {
        width: 9.67vw;
        height: 2.93vw;
      }

      // polygon
      :nth-of-type(4) {
        width: 11.72vw;
        height: 2.42vw;
      }

      // hashed
      :nth-of-type(5) {
        width: 10.73vw;
        height: 2.73vw;
      }
    }
  }
`;

const Logos3 = styled(Logos)`
  margin-bottom: 4.86vw;

  img {
    // tamarack global
    &:nth-of-type(1) {
      height: 1.23vw;
    }

    // svc
    &:nth-of-type(2) {
      width: 5.56vw;
      height: 1.95vw;
    }

    // three arrows
    &:nth-of-type(3) {
      width: 14.54vw;
      height: 2.42vw;
    }

    //gsr
    &:nth-of-type(4) {
      width: 6.25vw;
      height: 1.77vw;
    }
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {
      // tamarack global
      &:nth-of-type(1) {
        width: 29.3vw;
        height: 1.73vw;
      }

      // svc
      &:nth-of-type(2) {
        width: 7.81vw;
        height: 2.74vw;
      }

      // three arrows
      &:nth-of-type(3) {
        width: 20.51vw;
        height: 3.4vw;
      }

      //gsr
      &:nth-of-type(4) {
        width: 8.79vw;
        height: 2.49vw;
      }
    }
  }
`;

const Logos4 = styled(Logos)`
  margin-bottom: 4.86vw;

  img {

    // daedalus
    :nth-of-type(1) {
      width: 8.8vw;
      height: 1.92vw;
    }

    // everest ventures group
    &:nth-of-type(2) {
      width: 3.97vw;
      height: 3.29vw;
    }

    // upside partnership
    &:nth-of-type(3) {
      width: 15.07vw;
      height: 1.62vw;
    }

    // double peak
    &:nth-of-type(4) {
      width: 6.92vw;
      height: 6.92vw;
    }
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {

      // daedalus
      :nth-of-type(1) {
        width: 12.37vw;
        height: 2.7vw;
      }

      // everest ventures group
      &:nth-of-type(2) {
        width: 5.58vw;
        height: 4.62vw;
      }

      // upside partnership
      &:nth-of-type(3) {
        width: 21.19vw;
        height: 2.28vw;
      }

      // double peak
      &:nth-of-type(4) {
        width: 9.03vw;
        height: 3.13vw;
      }
    }
  }
`;

const Logos5 = styled(Logos)`
  margin-bottom: 4.86vw;

  img {
    // alumni ventures
    :nth-of-type(1) {
      width: 13.26vw;
      height: 1.39vw;
    }

    // cipholio
    &:nth-of-type(2) {
      width: 3.01vw;
      height: 3.29vw;
    }

    // xrm media
    &:nth-of-type(3) {
      width: 12.15vw;
      height: 1.29vw;
    }

    // ovo fund
    &:nth-of-type(4) {
      width: 6.52vw;
      height: 2.08vw;
    }
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {
      // alumni ventures
      :nth-of-type(1) {
        width: 18.65vw;
        height: 1.95vw;
      }

      // cipholio
      &:nth-of-type(2) {
        width: 4.23vw;
        height: 4.62vw;
      }

      // xrm media
      &:nth-of-type(3) {
        width: 17.09vw;
        height: 1.76vw;
      }

      // ovo fund
      &:nth-of-type(4) {
        width: 9.16vw;
        height: 2.93vw;
      }
    }
  }
`;

const Logos6 = styled(Logos)`
  margin-bottom: 4.86vw;
  
  img {
    // fwb
    :nth-of-type(1) {
      width: 5.03vw;
    }

    // twitch
    :nth-of-type(2) {
      width: 5.56vw;
    }

    //pugb
    :nth-of-type(3) {
      height: 2.13vw;
      width: 3.7vw;
    }

    // epic
    :nth-of-type(4) {
      width: 2.43vw;
    }

    //endeavor
    :nth-of-type(5) {
      width: 10.32vw;
    }
    
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {

      // fwb
      :nth-of-type(1) {
        width: 7.08vw;
      }
      
      // twitch
      :nth-of-type(2) {
        width: 7.82vw;
      }

      // pubg
      :nth-of-type(3) {
        width: 5.21vw;
        height: 3vw;
      }

      // epic
      :nth-of-type(4) {
        width: 3.42vw;
      }

      // meta
      :nth-of-type(5) {
        width: 14.79vw;
      }
    }
  }
`;

const Logos7 = styled(Logos)`
  margin-bottom: 4.86vw;

  img {
    // kowloon
    &:nth-of-type(1) {
      width: 6.57vw;
      height: 1.64vw;
    }
    
    //nba
    &:nth-of-type(2) {
      width: 1.39vw;
    }

    // pinterest
    :nth-of-type(3) {
      width: 7.18vw;
    }

    // microsoft
    :nth-of-type(4) {
      width: 7.7vw;
    }

    // meta
    :nth-of-type(5) {
      width: 7.52vw;
      height: 1.52vw;
    }
  }

  ${media.tablet} {
    margin-bottom: 6.84vw;

    img {
      // kowloon
      &:nth-of-type(1) {
        width: 9.17vw;
        height: 2.44vw;
      }

      // nba
      &:nth-of-type(2) {
        width: 1.92vw;
      }

      // pinterest
      :nth-of-type(3) {
        width: 10.1vw;
      }

      // microsoft
      :nth-of-type(4) {
        width: 10.82vw;
      }

      // meta
      :nth-of-type(5) {
        width: 10.58vw;
        height: 2.13vw;
      }
    }
  }
`;

const Accent = styled.div`
  white-space: pre;
  ${text.desktop.accent3};
  color: ${colors.black25};
  position: relative;
  z-index: 2;

  ${media.tablet} {
    ${text.tablet.accent3}
  }
`;

const HR = styled.div`
  background: ${colors.horzGradient};
  height: 2px;
  width: 100%;

  margin-top: 1.6vw;
  margin-bottom: 3.13vw;

  ${media.tablet} {
    margin-top: 1.95vw;
    margin-bottom: 4.88vw;
  }
`;

const Job = styled.p`
  color: ${colors.black25};
  ${text.desktop.bodyS};

  ${media.tablet} {
    ${text.tablet.bodyS}
  }
`;

const Name = styled.h6`
  ${text.desktop.accent2};
  background: ${colors.horzGradient};
  ${text.style.gradient};
  text-transform: uppercase;

  margin-bottom: 0.69vw;

  ${media.tablet} {
    ${text.tablet.accent2}
    margin-bottom: 0.98vw;
  }
`;

const Investors = styled.div`
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  flex-wrap: wrap;

  width: 35.76vw;

  ${media.tablet} {
    width: 50.39vw;
  }
`;

const Investor = styled.div`
  width: 10.76vw;
  height: 8.61vw;
  margin-bottom: 3.47vw;

  ${media.tablet} {
    width: 15.14vw;
    height: 12.99vw;
    margin-bottom: 4.88vw;
  }
`;

const HiddenWrapper = styled.div`
  overflow: hidden;
`;

const InvestorsWrapper = styled.div`
  width: 35.83vw;

  ${media.tablet} {
    width: 50.39vw;
  }
`;
